import React, { useEffect, useState } from "react";
import * as Survey from "survey-react";
import { get, isNil, isEmpty } from "lodash";
import { init as DropdownMultiselectWidgetInit } from "../Widgets/DropdownMultiselectWidget";
import { init as DropdownWidgetInit } from "../Widgets/DropdownWidget";
import { init as CalendarWidgetInit } from "../Widgets/CalendarWidget";
import buildResponses from "./buildResponses";
import { replaceIdWithUUID } from "../utils";

DropdownMultiselectWidgetInit(Survey);
DropdownWidgetInit(Survey);
CalendarWidgetInit(Survey);

// Override global messages
// console.log(Survey.surveyStrings);
Survey.surveyStrings.invalidExpression = "Expression is invalid";

// Adding custom property uuid to the question, and each choices
Survey.Serializer.addProperty("question", {
  name: "uuid:string",
  category: "general",
  readOnly: true,
  visible: true,
});

Survey.Serializer.addProperty("itemvalue", {
  name: "uuid:string",
  category: "general",
  readOnly: true,
  visible: true,
});

export default function SurveyJSFormRenderer({
  formData = {},
  formSubmission,
  editMode,
  handleSetSurveyModel,
  enableAccessibilityHacks = false,
}) {
  const [model, setModel] = useState(null);

  useEffect(() => {
    const configuration = formData.configuration;
    if (configuration) {
      // Replace id with uuid
      const json = replaceIdWithUUID(configuration);
      const newModel = new Survey.Model(json);
      newModel.showCompletedPage = false;

      //If there is a submission data, we need to fill the form with it
      if (!isEmpty(formSubmission)) {
        const data = {};
        formSubmission.responses.forEach((item) => {
          let deprecateResponse = false;
          let value = String(!isNil(item.value) ? item.value : "");
          const question = newModel.getQuestionByName(item.name);
          // if value has a comma it means we have multiples values (eg: dropdown-multiselect)
          if (value.indexOf(",") !== -1) {
            value = value.split(",");
          }
          // if value has no comma but has maxSelextedChoices as a property it means it should be in an array (eg: dropdown-multiselect)
          else if (question && question?.maxSelectedChoices >= 0) {
            value = [value];
          }
          // check for validity of old submission values against current survey/form choice options
          if (question && !isEmpty(question?.choices)) {
            // multi-choice iteration
            if (Array.isArray(value)) {
              let invalidValues = new Array();
              value.forEach((v) => {
                const validChoice = question.choices.find((c) => c.value === v);
                if (!validChoice) {
                  invalidValues.push(v);
                }
              });
              let validValues = value.filter((v) => !invalidValues.includes(v));
              // if all values map to deprecated choices, deprecate response
              if (validValues.length === 0) {
                deprecateResponse = true;
              } else {
                value = [...validValues];
              }
            }
            // single-choice iteration
            else {
              const validChoice = question.choices.find(
                (c) => c.value === value
              );
              if (!validChoice) {
                deprecateResponse = true;
              }
            }
          }

          if (!deprecateResponse) {
            data[item.name] = value;
          }
        });
        newModel.data = data;
      }

      // For test only (when running in localhost the preview tab shows "Complete" button)
      // newModel.onComplete.add(function (sender, options) {
      //   console.log('responses', buildResponses(newModel))
      // })

      setModel(newModel);
      handleSetSurveyModel(newModel);
    }
  }, [formSubmission]);

  // For readonly mode, show all the question with the answer
  if (!editMode) {
    const questionsNames = get(model, "questionHashes.names", {});
    const responses = get(formSubmission, "responses", []);
    const description = get(model, "description", "");
    const answerAtLeastOne =
      "Answer at least one question for a preview to appear here.";
    if (responses.length === 0) {
      return (
        <div className="normal-case answer-at-least-one">
          {answerAtLeastOne}
        </div>
      );
    } else {
      return (
        <div className="ui-form-renderer">
          {description && <h5 className="mb-5 normal-case">{description}</h5>}
          {Object.keys(questionsNames).map((questionName) => {
            const questionResponse = responses.find(
              (r) => r.name === questionName
            );
            let isHtml = false;
            let title;
            let displayValue;
            const question = model.getQuestionByName(questionName);
            if (!question) {
              return;
            }
            if (
              question.conditionRunner &&
              !model.runCondition(question.conditionRunner.expressionValue)
            ) {
              return;
            }
            if (question.isVisible && question.getType() === "html") {
              isHtml = true;
              title = get(question, "html");
              //Show HTML type elements
            } else if (!question.isVisible || !questionResponse) {
              //Hide unanswered question
              return;
            } else {
              ({ title, displayValue } = questionResponse);
            }

            if (!displayValue && title === "") {
              return;
            }

            const displayLabel = isHtml ? (
              <div
                className="ui-form-renderer-question-display__label"
                dangerouslySetInnerHTML={{ __html: title }}
              />
            ) : (
              <div className="ui-form-renderer-question-display__label">
                {title}
              </div>
            );

            return (
              <div className="ui-form-renderer-question-display" key={title}>
                {displayLabel}
                <div className="ui-form-renderer-question-display__value">
                  {displayValue}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  }

  return (
    model && (
      <Survey.Survey
        model={model}
        showQuestionNumbers="off"
        onUpdateQuestionCssClasses={(survey, options) => {
          // Custom css classes to match Unite Us styles
          const classes = options.cssClasses;
          classes.title +=
            " ui-form-field__label ui-label-roman ui-label-roman--lowercase";
          if (options.question.getType() === "text") {
            classes.mainRoot += " ui-input-field ui-form-field";
          }
          if (options.question.getType() === "comment") {
            classes.mainRoot += " ui-text-field ui-form-field";
          }
          if (options.question.getType() === "radiogroup") {
            classes.mainRoot += " ui-radio-field ui-form-field";
          }
          if (options.question.getType() === "checkbox") {
            classes.mainRoot += " ui-checkbox-group ui-form-field";
            classes.item += " ui-checkbox-field ui-form-field";
            classes.controlLabel += " ui-form-field__label";
          }
          if (
            options.question.getType() === "dropdown-multiselect" ||
            options.question.getType() === "dropdown" ||
            (options.question.getType() === "text" &&
              options.question.inputType === "date")
          ) {
            classes.mainRoot = "sv_q overflow-visible";
          }
        }}
        onAfterRenderQuestion={(survey, options) => {
          if (enableAccessibilityHacks) {
            //Change surveyjs default question heading to label instead of h5
            options.htmlElement
              .querySelectorAll(".sv_q_title")
              .forEach((el) => {
                const replacement = document.createElement("label");
                // add existing h5 element attributes to the new label element
                if (el.hasAttributes()) {
                  for (const attr of el.attributes) {
                    replacement.setAttribute(attr.name, attr.value);
                  }
                }
                // set innerhtml of new label element
                replacement.innerHTML = el.innerHTML;
                // replace h5 with label
                el.replaceWith(replacement);
              });

            //Remove surveyjs question div validation aria attributes
            options.htmlElement.removeAttribute("aria-required");
            options.htmlElement.removeAttribute("aria-invalid");

            //Remove choicesjs aria attributes
            options.htmlElement
              .querySelectorAll(".choices__item")
              .forEach(function (el) {
                el.removeAttribute("aria-selected");
              });
            options.htmlElement
              .querySelectorAll("[role=presentation]>div>.ui-select-field>div")
              .forEach(function (el) {
                el.removeAttribute("aria-label");
              });
          }
        }}
      />
    )
  );
}
